import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Search, Menu, X } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { SearchDialog } from "@/components/site/SearchDialog";
import { btnSecondary } from "@/components/site/buttons";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/articles", label: "Articles" },
  { to: "/videos", label: "Videos" },
  { to: "/services", label: "Services" },
  { to: "/financial-planning", label: "Financial Planning" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function Nav() {
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setMenuOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Clicking the link for the page you're already on should return to the top.
  const scrollTopIfSame = (to) => () => {
    if (location.pathname === to) window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <header
        className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
          scrolled ? "border-gold-500/25 bg-cream-100/85 backdrop-blur-xl" : "border-transparent bg-cream-100/70 backdrop-blur-md"
        }`}
        data-testid="site-nav"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-6 px-5 py-4 lg:px-10">
          <Logo />

          <nav className="hidden items-center gap-7 lg:flex">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={scrollTopIfSame(l.to)}
                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-sans text-sm tracking-wide transition-colors gf-link ${
                    isActive ? "text-gold-600" : "text-ink/80 hover:text-gold-600"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              data-testid="nav-search-button"
              className="text-ink/70 transition-colors hover:text-gold-600"
            >
              <Search className="h-5 w-5" />
            </button>
            <Link to="/contact" className={`hidden md:inline-flex ${btnSecondary}`} data-testid="nav-consult-button">
              Request a Consultation
            </Link>
            <button
              className="text-ink lg:hidden"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Menu"
              data-testid="nav-menu-toggle"
            >
              {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="border-t border-gold-500/20 bg-cream-100 lg:hidden" data-testid="mobile-menu">
            <nav className="mx-auto flex max-w-7xl flex-col px-5 py-2">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  onClick={scrollTopIfSame(l.to)}
                  className={({ isActive }) =>
                    `border-b border-cream-200 py-3 font-sans text-base ${isActive ? "text-gold-600" : "text-ink/80"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/contact" className={`my-4 ${btnSecondary}`}>
                Request a Consultation
              </Link>
            </nav>
          </div>
        )}
      </header>
      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
